import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ClockCounterClockwise, Warning, WarningCircle, CheckCircle, ArrowSquareOut, CaretDown, CaretRight, Repeat } from "@phosphor-icons/react";

// Compact panel showing prior jobs at the same property (past work orders / repeat problems).
export default function PropertyHistoryPanel({ address, currentJobId, adminLink = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!address || address.length < 3) { setData({ matches: 0, jobs: [], recurring_categories: [] }); return; }
    api.get(`/properties/history`, { params: { address, exclude_id: currentJobId, limit: 30 }})
      .then(r => setData(r.data))
      .catch(() => setData({ matches: 0, jobs: [], recurring_categories: [] }));
  }, [address, currentJobId]);

  if (!data) return null;
  const { matches, jobs = [], recurring_categories = [], repeat_alerts = [], repeat_threshold = 3 } = data;
  const alertSet = new Set(repeat_alerts.map(a => a.issue));
  const linkPrefix = adminLink ? "/admin/jobs" : "/worker/jobs";

  return (
    <div className="rugged-card p-4 bg-white" data-testid="property-history-panel">
      <button onClick={() => setOpen(v => !v)}
        data-testid="property-history-toggle"
        className="w-full flex items-center justify-between gap-2 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <ClockCounterClockwise weight="bold" size={20} className="text-slate-700 flex-shrink-0"/>
          <div className="min-w-0">
            <div className="font-display font-black text-sm uppercase tracking-wider">Property History</div>
            <div className="text-xs text-slate-500 truncate">
              {matches === 0 ? "First visit at this address" : `${matches} prior work order${matches === 1 ? "" : "s"}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {recurring_categories.length > 0 && (
            <span className="hidden sm:inline-flex px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-amber-500 text-white items-center gap-1" title="Recurring category detected">
              <Warning weight="fill" size={11}/> Repeat
            </span>
          )}
          {matches > 0 && (open ? <CaretDown weight="bold" size={16}/> : <CaretRight weight="bold" size={16}/>)}
        </div>
      </button>

      {/* Repeat-issue alerts — same issue 3+ times here. Red, always visible. */}
      {repeat_alerts.length > 0 && (
        <div className="mt-3 p-3 border-l-4 border-red-600 bg-red-50" data-testid="repeat-issue-alerts">
          <div className="text-[10px] font-black uppercase tracking-wider text-red-900 flex items-center gap-1">
            <WarningCircle weight="fill" size={12}/> Repeat Issue — {repeat_threshold}+ times at this property
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {repeat_alerts.map(a => (
              <span key={a.issue} data-testid={`repeat-issue-${a.issue}`}
                title={`First ${(a.first_at || "").slice(0, 10)} · Last ${(a.last_at || "").slice(0, 10)}`}
                className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-white border-2 border-red-600 text-red-800">
                {a.issue} × {a.count}{a.open ? ` · ${a.open} open` : ""}
              </span>
            ))}
          </div>
          <div className="mt-1 text-[10px] text-red-800 font-bold">Check history below before dispatching — likely a root cause, not a one-off.</div>
        </div>
      )}

      {/* Recurring-category chips (always visible if any) */}
      {recurring_categories.filter(r => !alertSet.has(r.category)).length > 0 && (
        <div className="mt-3 p-3 border-l-4 border-amber-500 bg-amber-50" data-testid="recurring-chips">
          <div className="text-[10px] font-black uppercase tracking-wider text-amber-900 flex items-center gap-1">
            <Repeat weight="bold" size={12}/> Recurring Problems Here
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {recurring_categories.filter(r => !alertSet.has(r.category)).map(r => (
              <span key={r.category} data-testid={`recur-${r.category}`}
                className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-white border-2 border-amber-500 text-amber-800">
                {r.category} × {r.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Job list */}
      {open && matches > 0 && (
        <div className="mt-3 space-y-2" data-testid="property-history-list">
          {jobs.map(j => (
            <Link key={j.id} to={`${linkPrefix}/${j.id}`}
              data-testid={`history-job-${j.id}`}
              className="block p-2 border border-slate-300 hover:border-[#0f172a] hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-wider font-mono bg-[#0f172a] text-white px-1.5 py-0.5">
                      {j.work_order_number || "—"}
                    </span>
                    {j.category && (
                      <span className="text-[10px] font-black uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5">
                        {j.category}
                      </span>
                    )}
                    {j.unit && j.unit !== "1" && (
                      <span className="text-[10px] font-black uppercase tracking-wider bg-fuchsia-600 text-white px-1.5 py-0.5">Unit {j.unit}</span>
                    )}
                    {j.is_turn && (
                      <span className="text-[10px] font-black uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5">Turn</span>
                    )}
                    {j.source === "appfolio" && (
                      <span className="text-[10px] font-black uppercase tracking-wider bg-white border border-slate-400 text-slate-600 px-1.5 py-0.5" data-testid={`history-appfolio-${j.id}`}>AppFolio</span>
                    )}
                    {j.status === "completed" ? (
                      <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 inline-flex items-center gap-0.5">
                        <CheckCircle weight="fill" size={9}/> Done
                      </span>
                    ) : (
                      <span className="text-[10px] font-black uppercase tracking-wider bg-slate-500 text-white px-1.5 py-0.5">{j.status}</span>
                    )}
                  </div>
                  <div className="mt-1 text-sm font-bold truncate">{j.title || "—"}</div>
                  {j.completion_notes && (
                    <div className="mt-0.5 text-xs text-slate-600 truncate">{j.completion_notes}</div>
                  )}
                  <div className="mt-0.5 text-[10px] text-slate-500">
                    {(j.completed_at || j.created_at || "").slice(0, 10)}
                    {j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}
                  </div>
                </div>
                <ArrowSquareOut weight="bold" size={14} className="text-slate-400 flex-shrink-0"/>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
